import React, { useEffect, useMemo, useState } from 'react';
import { FormikErrors, useFormik } from 'formik';
import Yup from '~yup';

import Text from '~components/atoms/Text';
import ActionButton from '~components/atoms/ActionButton';
import LabelledInput from '~components/atoms/LabelledInput';
import LabelledDropdown from '~components/atoms/LabelledDropdown';

import combinedActions from '~redux/combinedActions';
import { useAppDispatch, useAppSelector } from '~redux/hooks';
import { BranchStatusEnum, CustomerAccFundTypeEnum, CustomerAccTypeEnum, CustomerIdTypeEnum, CustomerRaceEnum, DiscountStatusEnum, FundTypeEnum, GoldTypeEnum, MetalTypeEnum, SilverTypeEnum, TransactionActionEnum } from '~utils/enums';

import { TBuyOrSellMultipleProductsParams, TGetTotalNetValueOrQuantityReduxParams, TInventoryFilter } from '~types/feat/gold';
import { TDropdownOption } from '~types/components/labelledDropdown';

import * as FormStyles from './_goldTransactionForm.module.scss';
import Checkbox from '~components/atoms/Checkbox';
import LabelledDatepicker from '~components/atoms/LabelledDatepicker';
import dayjs from 'dayjs';
import { Country } from 'react-country-state-city/dist/esm/types';
import countryList from '~utils/country';
import { IoClose } from 'react-icons/io5';

// ? validation
const getBuyGoldSchema = (
    transactionType: TransactionActionEnum,
    branchesList: TDropdownOption[],
    countries: Country[],
    availableSerialNoList?: string[]
) => Yup.object().shape({
    name: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().required('Customer name is required'),
    idNo: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().required('Customer ID is required'),
    CIFNo: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().required('Customer CIF is required'),
    branchName: Yup.string()
        .matchNoLeadingAndTrailingWhiteSpaces()
        .matchMaxOneWhiteSpace()
        .required('Branch name is required')
        .test('check-branch-name',
            'Selected branch does not exist',
            function (value) {
                if (!value) return false;
                return branchesList.some((branch) => branch.label === value);
            }
        ),
    branchCode: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().required('Branch code is required'),
    region: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().required('Region is required'),
    metalType: Yup.number()
        .required('Metal Type is required')
        .min(1, 'Metal Type is required'),
    products: Yup.array().of(
        Yup.object({
            productId: Yup.number().required('Product ID is required'),
            productName: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().required('Product Name is required'),
            // goldType: Yup.number().required('Product Gold Type missing'),
            // silverType: Yup.number().required('Product Silver Type missing'),
            availableQuantity: Yup.string(),
            amount: Yup.number()
                .test(
                    'is-valid-quantity',
                    'Please enter a valid number',
                    function (value) {
                        if (!value) return false;
                        return !isNaN(value);
                    }
                )
                .test('is-within-balance',
                    'Quantity must not exceed available balance',
                    function (value) {
                        const { availableQuantity } = this.parent;
                        const firstWord = availableQuantity.split(' ')[0];

                        if (isNaN(Number(firstWord))) return false;
                        if (!value) return false;

                        return value <= firstWord;
                    })
                .typeError('Quantity must be a number')
                .positive('Quantity must be positive')
                .max(1000000, 'Gold amount must not exceed 1000000 grams')
                .required('Quantity is required'),
            goldAmountMYR: Yup.string().required('Gold amount in MYR is required'),
            serialNo:
                Yup.string()
                    .matchNoLeadingAndTrailingWhiteSpaces()
                    .test('serial-numbers-count',
                        function (value) {
                            const { amount, goldType, silverType } = this.parent;

                            if (goldType === GoldTypeEnum.PaperGold) return true;
                            if (silverType === SilverTypeEnum.PaperSilver) return true;

                            if (!value) return this.createError({
                                message: 'Must provide a serial number',
                            });

                            const serialNumbers = value.split(',').map(s => s.trim()).filter(s => s.length > 0);

                            if (serialNumbers.length !== amount) {
                                return this.createError({
                                    message: `Must provide exactly ${amount} serial number${amount !== 1 ? 's' : ''}`,
                                });
                            }

                            return serialNumbers.length === amount;
                        }
                    )
                    .test('available-serial-numbers',
                        'The provided serial numbers are not available',
                        function (value) {
                            const { goldType, silverType } = this.parent;

                            if (goldType === GoldTypeEnum.PaperGold) return true;
                            if (silverType === SilverTypeEnum.PaperSilver) return true;

                            if (!value) return false;
                            if (!availableSerialNoList) return false;

                            const valuesArray = value.split(',').map(s => s.trim()).filter(s => s.length > 0);
                            return valuesArray.every(serialNo => availableSerialNoList.includes(serialNo));
                        }
                    )
                    .test('duplicate-serial-numbers',
                        'The provided serial numbers are not unique',
                        function (value) {
                            const { goldType, silverType } = this.parent;

                            if (goldType === GoldTypeEnum.PaperGold) return true;
                            if (silverType === SilverTypeEnum.PaperSilver) return true;

                            if (!value) return false;
                            const valuesArray = value.split(',').map(s => s.trim()).filter(s => s.length > 0);
                            return valuesArray.length === new Set(valuesArray).size;
                        }
                    ),
            totalNetValue: Yup.string()
                .matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace()
                .required('Total net value is required').test('is-not-zero', 'Total net value must not be zero', (value) => {
                    const val = parseFloat(value);
                    return val !== 0;
                }),
            discountCode: Yup.string().matchNoLeadingAndTrailingWhiteSpaces(),
        }),
    ),
    salesPersonnelName: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace(),
    referralName: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace(),
    projectName: Yup.string(),
    isEarmarkTransaction: Yup.boolean().required(),
    earmarkEndDate: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().label('End Date'),
    baseAPIPrice: Yup.string(),
});

const BuyGoldForm = () => {
    const dispatch = useAppDispatch();

    const customerList = useAppSelector((state) => state.customer.allCustomers);
    const customerDetails = useAppSelector((state) => state.customer.customerDetails);
    const branchesList = useAppSelector((state) => state.branch.allBranches);
    const regionList = useAppSelector((state) => state.branch.allRegions);
    const inventoryList = useAppSelector((state) => state.gold.allInventories);
    const serialNumberList = useAppSelector((state) => state.gold.allSerialNumbers);
    const discountList = useAppSelector((state) => state.setting.allDiscounts);

    const transactionType = useAppSelector((state) => state.gold.buyAndSellGold.transactionType);
    const isFormFilled = useAppSelector((state) => state.gold.modals.isFormFilled);

    const buyOrSellGoldParams = useAppSelector((state) => state.gold.params.buyOrSellGoldParams);
    const inventoryFilterParams = useAppSelector((state) => state.gold.params.inventoryFilterParams);
    const serialNumberFilterParams = useAppSelector((state) => state.gold.params.serialNumberFilterParams);

    const goldPrices = useAppSelector((state) => state.gold.buyAndSellGold.goldPrices);
    const silverPrices = useAppSelector((state) => state.silver.buyAndSellSilver.silverPrices);

    const customerInventory = useAppSelector((state) => state.customer.customerInventory);

    const searchCustomerIsLoading = useAppSelector(state => state.customer.loadings.getAllCustomers);

    const { allBranchAccess, branchCode } = useAppSelector((state) => state.auth.userDetails);

    const projectFilters = useAppSelector(state => state.project.projectFilters);

    const earmarkBalance = useAppSelector((state) => state.customer.customerDetails.earmarkBalance);

    const [amountOnBlurError, setAmountOnBlurError] = useState('');
    const [totalNetValueOnBlurError, setTotalNetValueOnBlurError] = useState('');
    const [discountOptions, setDiscountOptions] = useState<TDropdownOption[]>([]);
    const [allAvailableSerialNo, setAllAvailableSerialNo] = useState<string[]>([]);

    const [name, setName] = useState('');
    const [productSearch, setProductSearch] = useState(
        [
            {
                productName: '',
            },
        ]
    );
    const [selectedBranchCode, setSelectedBranchCode] = useState('');
    const [gettingCustomers, setGettingCustomers] = useState(false);
    const [disableCustomerSearch, setDisableCustomerSearch] = useState(false);
    const [countries, setCountries] = useState<Country[]>([]);
    const [endDateError, setEndDateError] = useState<string>('');

    const [totalNetValueApiParams, setTotalNetValueApiParams] = useState<TGetTotalNetValueOrQuantityReduxParams>({
        index: undefined,
        productId: 0,
        baseAPIPrice: 0,
        pricePerGram: 0,
        transactionType: TransactionActionEnum['Bank Sell'],
    });

    const customerOptions = customerList.map((customer) => ({
        label: `${customer.name} - ${customer.idNo}  (${CustomerAccFundTypeEnum[customer.fundType]}) (${CustomerAccTypeEnum[customer.accountType]})${customer.accountType === CustomerAccTypeEnum.Joint ? (' - ' + customer.childName) : ''}`,
        value: customer.customerId,
    }));

    const branchOptions = branchesList.filter(branch => branch.status === BranchStatusEnum.Active).map((branch) => ({
        label: branch.branchName,
        value: branch.branchCode,
    }));

    const regionOptions = regionList.map((region) => ({
        label: region.regionName,
        value: region.regionName,
    }));

    const inventoryOptions = (index: number) => {
        if (!inventoryList?.length) return [];

        const selectedMetalType = formik.values.metalType;
        const customerFundType = customerDetails.fundType;

        let result = inventoryList;

        result = result.filter(item =>Number(item.fundType) === Number(customerFundType));

        const isGoldSelected = selectedMetalType === MetalTypeEnum.Gold;
        const isSilverSelected = selectedMetalType === MetalTypeEnum.Silver;

        result = result.filter(item => {
            if (!selectedMetalType) return true;

            if (isGoldSelected) {
                return item.goldType != null;
            }

            if (isSilverSelected) {
                return item.silverType != null;
            }

            return true;
        });

        if (transactionType === TransactionActionEnum['Bank Buy']) {
            result = result.filter(item =>
                customerInventory.some(elem => elem.productId === item.productId)
            );
        }

        // remove already selected
        const selectedIds = formik.values.products
            .map(p => p.productId)
            .filter(Boolean);

        result = result.filter(item => !selectedIds.includes(item.productId));

        return result.map(item => ({
            label: item.productName,
            value: item.productId,
        }));
    };

    const getBaseAPIPrice = (metalType: MetalTypeEnum) => {
        const prices = metalType === MetalTypeEnum.Gold ? goldPrices : silverPrices;

        if (transactionType === TransactionActionEnum['Bank Buy']) {
            return prices.gbuy;
        }

        return prices.gsell;
    };

    const BuyGoldSilverInitialValues = {
        customerId: 0,
        name: '',
        idNo: '',
        CIFNo: '',
        branchName: '',
        branchCode: '',
        region: '',
        metalType: 0,
        products: [{
            productId: 0,
            productName: '',
            goldType: 0,
            silverType: 0,
            availableQuantity: 'Please select customer first',
            collateralQuantity: 'Please select customer first',
            amount: '',
            serialNo: '',
            goldAmountMYR: '',
            totalNetValue: '',
            discountId: 0,
            discountCode: '',
            discountName: '',
        }],
        fundType: 0,
        salesPersonnelName: '',
        referralName: '',
        projectName: '',
        projectId: 0,
        isEarmarkTransaction: false,
        earmarkEndDate: '',
        baseAPIPrice: '',
    };

    const formik = useFormik({
        initialValues: BuyGoldSilverInitialValues,
        validationSchema: getBuyGoldSchema(
            transactionType,
            branchOptions,
            countries,
            allAvailableSerialNo,
        ),
        onSubmit: (values, { setSubmitting }) => {
            setEndDateError('');

            const productDraft = values.products.map((product) => ({
                productId: product.productId,
                ...(values.metalType === MetalTypeEnum.Gold
                    ? {
                        goldPrice: Number(product.goldAmountMYR),
                    }
                    : {
                        silverPrice: Number(product.goldAmountMYR),
                    }),
                amount: Number(product.amount),
                serialNo: product.serialNo.replace(/\s+/g, '').split(','),
                discountId: product.discountId || undefined,
            }));

            if (values.isEarmarkTransaction) {
                if (values.earmarkEndDate === '') {
                    setEndDateError('Select the End Date');
                    setSubmitting(false);
                    return;
                }
                if (values.projectName === '') {
                    setEndDateError('Select a Project');
                    setSubmitting(false);
                    return;
                }
                // if (values.goldType === GoldTypeEnum.PhysicalGold) {
                //     setEndDateError('Earmark is for Paper Gold product only!');
                //     setSubmitting(false);
                //     return;
                // }

                dispatch(combinedActions.goldSetBuyOrSellGoldParams(values));
                dispatch(combinedActions.goldSetIsFormFilled(true));
                dispatch(combinedActions.goldCreateTransactionDraftAttempt({
                    customerId: values.customerId,
                    action: transactionType,
                    baseAPIPrice: Number(values.baseAPIPrice),
                    items: productDraft,
                    isEarmarkTransaction: values.isEarmarkTransaction,
                    projectId: values.projectId,
                    metalType: values.metalType,
                }));
            }

            if (!values.isEarmarkTransaction) {
                const {
                    earmarkEndDate,
                    ...restOfValues
                } = values;

                dispatch(combinedActions.goldSetBuyOrSellGoldParams(restOfValues));
                dispatch(combinedActions.goldSetIsFormFilled(true));
                dispatch(combinedActions.goldCreateTransactionDraftAttempt({
                    customerId: values.customerId,
                    action: transactionType,
                    baseAPIPrice: Number(values.baseAPIPrice),
                    items: productDraft,
                    isEarmarkTransaction: values.isEarmarkTransaction,
                    projectId: values.projectId,
                    metalType: values.metalType,
                }));
            }
        },
    });

    const nationalityOptions = Object.values(countries).map(country => {
        const countryName = country.name;
        return {
            label: countryName,
            value: countryName,
        };
    });

    const raceOptions = Object.values(CustomerRaceEnum).filter(e => Number.isNaN(Number(e))).map(key => {
        return {
            label: key as string,
            value: key as string,
        };
    });

    const idTypeOptions = Object.values(CustomerIdTypeEnum).filter(e => Number.isNaN(Number(e))).map(key => {
        return {
            label: key as string,
            value: key as string,
        };
    });

    const projectOptions = projectFilters?.map(project => ({
        label: `${project.projectName} (RM ${project.earmarkAmount}, ${project.earmarkEndDate} Years)`,
        value: project.projectId,
    })) || [];

    useEffect(() => {
        dispatch(combinedActions.projectGetProjectFiltersAttempt());
    }, [dispatch]);

    useEffect(() => {
        const massagedResult = [
            countryList.find(item => item.name === 'Malaysia'),
            ...countryList,
        ].filter(item => item);

        setCountries(massagedResult as Country[]);
    }, []);

    // ? to set branch based on user details
    useEffect(() => {
        if (!allBranchAccess) {
            const branch = branchesList.find((branchOption) => branchOption.branchCode === branchCode);
            const branchRegion = regionList.find((regionOption) => regionOption.regionId === branch?.regionId);

            if (branch) {
                setSelectedBranchCode(branch.branchCode);
                dispatch(combinedActions.goldSetSerialNumberFilterParams({
                    ...serialNumberFilterParams,
                    branchCode: branch.branchCode,
                }));

                formik.setFieldValue('branchName', branch.branchName);
                formik.setFieldValue('branchCode', branch.branchCode);
            }

            if (branchRegion) {
                formik.setFieldValue('region', branchRegion.regionName);
            }
        }
    }, [branchesList, goldPrices, silverPrices]);

    // ? to get selected branch inventories
    useEffect(() => {
        if (selectedBranchCode && !formik.values.products[0].productId) {
            dispatch(combinedActions.goldSetInventoryFilterParams({ ...inventoryFilterParams, branchCode: selectedBranchCode }));
            dispatch(combinedActions.goldGetAllInventoriesAttempt());
            formik.setValues({
                ...formik.values,
                branchCode: selectedBranchCode,
                products: [{
                    productId: 0,
                    silverType: 0,
                    goldType: 0,
                    productName: '',
                    availableQuantity: 'Please select product first',
                    collateralQuantity: 'Please select product first',
                    amount: '',
                    serialNo: '',
                    goldAmountMYR: '',
                    totalNetValue: '',
                    discountId: 0,
                    discountCode: '',
                    discountName: '',
                }],
            }); // to reset product each time changing branch
        }
    }, [selectedBranchCode]);

    // ? to reassigned value when going back from summary
    useEffect(() => {
        if (!isFormFilled) {

            // check if product was previously selected
            if (buyOrSellGoldParams.products.length <= 0) return;

            // check if product is valid
            const product = inventoryList.find((productOption) => productOption.productId === buyOrSellGoldParams.products[0].productId);

            if (!product) return;

            setName(`${buyOrSellGoldParams.name} - ${buyOrSellGoldParams.idNo}  (${CustomerAccFundTypeEnum[buyOrSellGoldParams.fundType]})`);
            setSelectedBranchCode(buyOrSellGoldParams.branchCode);

            let productSearchArray: {productName: string}[] = [];
            buyOrSellGoldParams.products.map((item) => {
                productSearchArray = [
                    ...productSearchArray,
                    {
                        productName: item.productName,
                    },
                ];

                dispatch(combinedActions.goldSetSerialNumberFilterParams({
                    action: transactionType,
                    branchCode: buyOrSellGoldParams.branchCode,
                    customerId: buyOrSellGoldParams.customerId,
                    productId: item.productId,
                }));
                dispatch(combinedActions.goldGetAllSerialNumbersAttempt());
            });

            setProductSearch(productSearchArray);

            formik.setValues({
                ...formik.values,
                ...buyOrSellGoldParams,
            });
        }
    }, [isFormFilled]);

    useEffect(() => {
        let timeout: NodeJS.Timeout;

        if (name && !disableCustomerSearch) {
            setGettingCustomers(true);

            // debounce customer name input
            timeout = setTimeout(() => {
                dispatch(combinedActions.customerGetAllCustomersAttempt({ searchQuery: name }));
            }, 1000);
        } else setGettingCustomers(false);

        setDisableCustomerSearch(false);

        return () => {
            setGettingCustomers(false);
            clearTimeout(timeout);
        };
    }, [name]);

    useEffect(() => {
        // to set loading state (debouncing causes loading state to also delay hence we use a local loading state)
        if (!searchCustomerIsLoading) {
            setGettingCustomers(false);
        }
    }, [searchCustomerIsLoading]);

    useEffect(() => {
        const now = dayjs();

        if (discountList) {
            const filteredListing = discountList.filter((item) =>
                item.transactionType === transactionType
                && item.status === DiscountStatusEnum.Active
                && !dayjs(item.discountEndDate).isBefore(now)
            );

            if (!filteredListing) return;

            const newList = filteredListing.map((discount) => ({
                value: discount.discountId,
                label: discount.discountCode,
            }));

            const massagedList = [
                {
                    value: '',
                    label: 'Select Discount',
                },
                ...newList,
            ];

            setDiscountOptions(massagedList);

            return;
        }

        return setDiscountOptions([]);
    }, [discountList]);

    useEffect(() => {
        formik.values.products.map((product) => {
            const selectedProduct = getSelectedProduct(product.productId);

            if (transactionType === TransactionActionEnum['Bank Sell']) {
                const newSerialNumber = serialNumberList.map((serialNumber) => serialNumber.serialNo);

                if (newSerialNumber.every(serialNo => allAvailableSerialNo.includes(serialNo))) return;

                setAllAvailableSerialNo([...allAvailableSerialNo, ...newSerialNumber]);
            }
            if (transactionType === TransactionActionEnum['Bank Buy'] && selectedProduct) {
                const newSerialNumber = customerInventory.find((customInventory) => customInventory.productId === selectedProduct?.productId)?.serialNo;

                if (!newSerialNumber) return;

                if (newSerialNumber.every(serialNo => allAvailableSerialNo.includes(serialNo))) return;

                setAllAvailableSerialNo([...allAvailableSerialNo, ...newSerialNumber]);
            }
        });
    }, [serialNumberList, customerInventory]);

    useEffect(() => {
        if (totalNetValueApiParams.index !== undefined) {
            if (formik.values.products[totalNetValueApiParams.index]) {
                if (totalNetValueApiParams.quantity !== undefined) {
                    formik.setFieldValue(
                        `products.[${totalNetValueApiParams.index}].totalNetValue`,
                        buyOrSellGoldParams.products[totalNetValueApiParams.index].totalNetValue.toString()
                    );
                }

                if (totalNetValueApiParams.totalNetValue !== undefined) {
                    const backCalcAmount = buyOrSellGoldParams.products[totalNetValueApiParams.index].amount;

                    formik.setFieldValue(
                        `products.[${totalNetValueApiParams.index}].amount`,
                        backCalcAmount
                    );

                    const product = getSelectedProduct(
                        formik.values.products[totalNetValueApiParams.index].productId
                    );
                    if (product && backCalcAmount) {
                        setProductSerialNumber(Number(backCalcAmount), product, totalNetValueApiParams.index);
                    }
                }

                formik.handleChange({
                    target: {
                        name: `products.[${totalNetValueApiParams.index}].goldAmountMYR`,
                        value: Number(
                            buyOrSellGoldParams.products[totalNetValueApiParams.index || 0].goldAmountMYR
                        ).toFixed(2),
                    },
                });
            }
        }
    }, [totalNetValueApiParams, buyOrSellGoldParams.products]);

    const getSelectedProduct = (productId: number) => {
        if (productId > 0) {
            const selectedProduct = inventoryList.find((inventory) => inventory.productId === productId);
            return selectedProduct;
        }

        return;
    };

    const getCustomerBalance = (selectedProduct?: TInventoryFilter, index?: number) => {
        if (selectedProduct !== undefined && index !== undefined) {
            dispatch(combinedActions.goldGetAllSerialNumbersAttempt());
            if (transactionType === TransactionActionEnum['Bank Sell']) {
                if (
                    selectedProduct.goldType === GoldTypeEnum.PhysicalGold ||
                    selectedProduct.silverType === SilverTypeEnum.PhysicalSilver
                ) {
                    formik.handleChange({
                        target: {
                            name: `products.[${index}]`, value: {
                                ...formik.values.products[index],
                                amount: '',
                                serialNo: '',
                                totalNetValue: '',
                                productId: Number(selectedProduct.productId),
                                productName: selectedProduct.productName,
                                goldType: selectedProduct.goldType,
                                silverType: selectedProduct.silverType,
                                availableQuantity: `${selectedProduct.amount ? selectedProduct.amount.toString() : 0} pcs`,
                                collateralQuantity: `${selectedProduct.collateralQuantity ?? 0}`,
                            },
                        },
                    });
                } else if (
                    selectedProduct.goldType === GoldTypeEnum.PaperGold ||
                    selectedProduct.silverType === SilverTypeEnum.PaperSilver
                ) {
                    const paperWeight = selectedProduct.goldType === GoldTypeEnum.PaperGold
                        ? selectedProduct.paperGoldWeight
                        : selectedProduct.paperSilverWeight;

                    formik.handleChange({
                        target: {
                            name: `products.[${index}]`, value: {
                                ...formik.values.products[index],
                                amount: '',
                                serialNo: '',
                                totalNetValue: '',
                                productId: Number(selectedProduct.productId),
                                productName: selectedProduct.productName,
                                goldType: selectedProduct.goldType,
                                silverType: selectedProduct.silverType,
                                availableQuantity: `${paperWeight ? paperWeight.toFixed(3) : 0} g`,
                                collateralQuantity: `${selectedProduct.collateralQuantity ? selectedProduct.collateralQuantity.toFixed(3) : 0}`,
                            },
                        },
                    });
                } else {
                    formik.handleChange({
                        target: {
                            name: `products.[${index}]`, value: {
                                ...formik.values.products[index],
                                amount: '',
                                serialNo: '',
                                totalNetValue: '',
                                productId: Number(selectedProduct.productId),
                                productName: selectedProduct.productName,
                                goldType: selectedProduct.goldType,
                                silverType: selectedProduct.silverType,
                                availableQuantity: '0',
                                collateralQuantity: '0',
                            },
                        },
                    });
                }

                return;
            }

            if (transactionType === TransactionActionEnum['Bank Buy']) {
                const selectedInventory = customerInventory.find((inventory) => inventory.productId === selectedProduct.productId);
                if (!selectedInventory) {
                    formik.handleChange({
                        target: {
                            name: `products.[${index}]`, value: {
                                ...formik.values.products[index],
                                amount: '',
                                serialNo: '',
                                totalNetValue: '',
                                productId: Number(selectedProduct.productId),
                                productName: selectedProduct.productName,
                                goldType: selectedProduct.goldType,
                                silverType: selectedProduct.silverType,
                                availableQuantity: 'Please select customer first',
                                collateralQuantity: 'Please select customer first',
                            },
                        },
                    });
                } else {
                    if (
                        selectedProduct.goldType === GoldTypeEnum.PhysicalGold ||
                        selectedProduct.silverType === SilverTypeEnum.PhysicalSilver
                    ) {
                        formik.handleChange({
                            target: {
                                name: `products.[${index}]`, value: {
                                    ...formik.values.products[index],
                                    amount: '',
                                    serialNo: '',
                                    totalNetValue: '',
                                    productId: Number(selectedProduct.productId),
                                    productName: selectedProduct.productName,
                                    goldType: selectedProduct.goldType,
                                    silverType: selectedProduct.silverType,
                                    availableQuantity: `${selectedInventory.amount ? selectedInventory.amount.toString() : 0} pcs`,
                                    collateralQuantity: `${selectedProduct.collateralQuantity ? selectedProduct.collateralQuantity.toFixed(3) : 0}`,
                                },
                            },
                        });
                    } else if (
                        selectedProduct.goldType === GoldTypeEnum.PaperGold ||
                        selectedProduct.silverType === SilverTypeEnum.PaperSilver
                    ) {
                        formik.handleChange({
                            target: {
                                name: `products.[${index}]`, value: {
                                    ...formik.values.products[index],
                                    amount: '',
                                    serialNo: '',
                                    totalNetValue: '',
                                    productId: Number(selectedProduct.productId),
                                    productName: selectedProduct.productName,
                                    goldType: selectedProduct.goldType,
                                    silverType: selectedProduct.silverType,
                                    availableQuantity: `${selectedInventory.amount ? selectedInventory.amount.toFixed(3) : 0} g`,
                                    collateralQuantity: `${selectedProduct.collateralQuantity ? selectedProduct.collateralQuantity.toFixed(3) : 0} g`,
                                },
                            },
                        });
                    }
                }

                return;
            }
        } else {
            if (transactionType === TransactionActionEnum['Bank Sell']) {
                formik.handleChange({ target: { name: `products.[${index}].availableQuantity`, value: 'Please select product first' } });
                formik.handleChange({ target: { name: `products.[${index}].collateralQuantity`, value: 'Please select product first' } });
            }

            if (transactionType === TransactionActionEnum['Bank Buy']) {
                formik.handleChange({ target: { name: `products.[${index}].availableQuantity`, value: 'Please select customer first' } });
                formik.handleChange({ target: { name: `products.[${index}].collateralQuantity`, value: 'Please select customer first' } });
            }
        }
    };

    const getProductSerialNumber = (product?: TInventoryFilter, customerId?: number) => {
        if (transactionType === TransactionActionEnum['Bank Buy']) {
            if (customerId) dispatch(combinedActions.customerGetCustomerInventoryAttempt({ customerId: customerId }));
        }

        if (transactionType === TransactionActionEnum['Bank Sell']) {
            if (product) {
                dispatch(combinedActions.goldSetSerialNumberFilterParams({
                    ...serialNumberFilterParams,
                    productId: product.productId,
                    action: transactionType,
                }));
            }
        }
    };

    const setProductSerialNumber = (quantity: number, selectedProduct: TInventoryFilter, index: number) => {
        if (selectedProduct && quantity) {
            if (transactionType === TransactionActionEnum['Bank Sell'] && selectedProduct.goldType === GoldTypeEnum.PhysicalGold) {
                const availableAmount = selectedProduct.amount;

                if (quantity > 0 && quantity <= availableAmount) {
                    if (quantity === availableAmount) {
                        const serialNumbers = serialNumberList.map((serialNumber) => serialNumber.serialNo);
                        formik.setFieldValue(`products.[${index}].serialNo`, serialNumbers.length > 1 ? serialNumbers.join(', ') : serialNumbers[0]);
                        return;
                    }

                    const serialNumbers = serialNumberList.slice(0, quantity).map((serialNumber) => serialNumber.serialNo);

                    formik.setFieldValue(`products.[${index}].serialNo`, serialNumbers.length > 1 ? serialNumbers.join(', ') : (serialNumbers[0] || ''));
                    return;
                } else {
                    formik.setFieldValue(`products.[${index}].serialNo`, '');
                    return;
                }
            } else if (selectedProduct.goldType === GoldTypeEnum.PaperGold) {
                formik.setFieldValue(`products.[${index}].serialNo`, 'N/A');
                return;
            }

            if (transactionType === TransactionActionEnum['Bank Buy'] && selectedProduct.goldType === GoldTypeEnum.PhysicalGold) {
                const productSerialNumber = customerInventory.find(
                    (item) => item.productId === selectedProduct.productId
                );

                if (!productSerialNumber) return;

                if (quantity > 0 && quantity <= productSerialNumber.amount) {
                    if (quantity === productSerialNumber.amount) {
                        formik.setFieldValue(`products.[${index}].serialNo`, productSerialNumber.serialNo.join(', '));
                        return;
                    }

                    formik.setFieldValue(`products.[${index}].serialNo`, quantity > 1 ? productSerialNumber.serialNo.slice(0, quantity).join(', ') : (productSerialNumber.serialNo[0] || ''));
                }
            } else formik.setFieldValue(`products.[${index}].serialNo`, 'N/A');
        }
    };

    const getIsSerialNoRequired = (index: number) => {
        const selectedProduct = getSelectedProduct(formik.values.products[index].productId);

        if (selectedProduct) {
            const serialNoRequired = formik.values.products && selectedProduct?.goldType === GoldTypeEnum.PhysicalGold;
            if (serialNoRequired) return true;
        }

        return false;
    };

    const handleOnSearchCustomer = (event: React.ChangeEvent<HTMLInputElement>) => {
        setName(event.target.value);

        const customer = customerList.find((customerOption) => customerOption.idNo === event.target.value);

        if (!customer) return;

        formik.setValues({
            ...formik.values,
            customerId: Number(customer.customerId),
            name: customer.name,
            idNo: customer.idNo,
            CIFNo: customer.CIFNo,
        });
    };

    const handleOnSelectCustomer = (option: TDropdownOption) => {
        const customer = customerList.find((customerOption) => customerOption.customerId === option.value);

        if (!customer) return;

        setDisableCustomerSearch(true);

        handleCustomerSelected(customer.customerId);

        formik.setValues({
            ...formik.values,
            customerId: Number(customer.customerId),
            name: customer.name,
            idNo: customer.idNo,
            CIFNo: customer.CIFNo,
            fundType: customer.fundType,
            metalType: 0,
            products: [{
                productId: 0,
                productName: '',
                goldType: 0,
                silverType: 0,
                availableQuantity: 'Please select product first',
                collateralQuantity: 'Please select product first',
                amount: '',
                serialNo: '',
                goldAmountMYR: '',
                totalNetValue: '',
                discountId: 0,
                discountCode: '',
                discountName: '',
            }],
            projectName: '',
            isEarmarkTransaction: false,
            earmarkEndDate: '',
            baseAPIPrice: '',
        });

        getCustomerBalance(undefined, 0);
        getProductSerialNumber(undefined, Number(customer.customerId));
    };

    const handleCustomerSelected = (customerId: number) => {
        dispatch(combinedActions.customerGetCustomerDetailsAttempt({ customerId }));
    };

    const handleOnSearchBranch = (event: React.ChangeEvent<HTMLInputElement>) => {
        formik.handleChange({ target: { name: 'branchName', value: event.target.value } });

        const branch = branchesList.find((branchOption) => branchOption.branchName === event.target.value);
        const branchRegion = regionList.find((regionOption) => regionOption.regionId === branch?.regionId);

        if (!branch) {
            dispatch(combinedActions.goldClearInventoryFilterParams());
            formik.handleChange({ target: { name: 'region', value: '' } });
            formik.handleChange({ target: { name: 'branchCode', value: '' } });
            setSelectedBranchCode('');
            return;
        }

        if (branch) {
            setSelectedBranchCode(branch.branchCode);
            formik.handleChange({ target: { name: 'branchCode', value: branch.branchCode } });
            dispatch(combinedActions.goldSetSerialNumberFilterParams({
                ...serialNumberFilterParams,
                branchCode: branch.branchCode,
            }));
        }

        if (branchRegion) {
            formik.handleChange({ target: { name: 'region', value: branchRegion.regionName } });
        }
    };

    const handleOnSelectBranch = (option: TDropdownOption) => {
        formik.handleChange({ target: { name: 'branchCode', value: option.value } });

        const branch = branchesList.find((branchOption) => branchOption.branchCode === option.value);
        const branchRegion = regionList.find((regionOption) => regionOption.regionId === branch?.regionId);

        if (branch) {
            setSelectedBranchCode(branch.branchCode);
            dispatch(combinedActions.goldSetSerialNumberFilterParams({
                ...serialNumberFilterParams,
                branchCode: branch.branchCode,
            }));

            formik.setValues({
                ...formik.values,
                products: [{
                    productId: 0,
                    silverType: 0,
                    goldType: 0,
                    productName: '',
                    availableQuantity: 'Please select product first',
                    collateralQuantity: 'Please select product first',
                    amount: '',
                    serialNo: '',
                    goldAmountMYR: '',
                    totalNetValue: '',
                    discountId: 0,
                    discountCode: '',
                    discountName: '',
                }],
            }); // to reset product each time changing branch
        }

        if (branchRegion) {
            formik.handleChange({ target: { name: 'region', value: branchRegion.regionName } });
        }
    };

    const handleOnSearchRegion = (event: React.ChangeEvent<HTMLInputElement>) => {
        formik.handleChange({ target: { name: 'region', value: event.target.value } });
    };

    const handleOnSelectRegion = (option: TDropdownOption) => {
        formik.handleChange({ target: { name: 'region', value: option.value } });
    };

    const handleOnSearchProduct = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
        // update displayed value only, without updating formik
        const updatedFilters = productSearch.map((item, i) =>
            i === index ? { productName: event.target.value } : item
        );
        setProductSearch(updatedFilters);

        const product = inventoryList.find((inventory) => inventory.productName === event.target.value);
        if (!product){
            formik.handleChange({ target: { name: `products.[${index}].productId`, value: 0 } });
            formik.handleChange({ target: { name: `products.[${index}].productName`, value: '' } });
        }

    };

    const handleOnSelectProduct = (option: TDropdownOption, index: number) => {
        const product = inventoryList.find((inventory) => inventory.productId === option.value);

        if (!product) return;
        if (formik.values.products[index].productId === product.productId) return;

        // update displayed value only, without updating formik
        const updatedFilters = productSearch.map((item, i) =>
            i === index ? { productName: product.productName } : item
        );
        setProductSearch(updatedFilters);

        dispatch(combinedActions.goldResetTransactionPricePerGramValue({ index }));

        setTotalNetValueApiParams({
            ...totalNetValueApiParams,
            index,
        });
        dispatch(combinedActions.goldGetTransactionPricePerGramValueAttempt({
            index,
            productId: product.productId,
            discountId: formik.values.products[index].discountId || undefined,
        }));

        getProductSerialNumber(product, undefined);
        getCustomerBalance(product, index);
    };

    const handleOnChangeQuantity = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
        formik.handleChange(event);

        if (amountOnBlurError) setAmountOnBlurError('');
        if (totalNetValueOnBlurError) setTotalNetValueOnBlurError('');

        if (event.target.value === '') {
            formik.setFieldValue(`products.[${index}]`, {
                ...formik.values.products[index],
                totalNetValue: '',
                amount: '',
                serialNo: '',
            });

            return;
        } else if (event.target.value === '0') {
            formik.setFieldValue(`products.[${index}]`, {
                ...formik.values.products[index],
                totalNetValue: '',
                amount: '0',
                serialNo: '',
            });

            return;
        } else {
            const productDetails = getSelectedProduct(formik.values.products[index].productId);

            if (productDetails) {
                setProductSerialNumber(Number(event.target.value), productDetails, index);

                dispatch(combinedActions.goldGetTotalNetValueOrQuantityAttempt({
                    index,
                    productId: productDetails?.productId,
                    baseAPIPrice: Number(formik.values.baseAPIPrice),
                    pricePerGram: Number(formik.values.products[index]?.goldAmountMYR || 0),
                    transactionType: transactionType,
                    discountId: formik.values.products[index].discountId || undefined,
                    quantity: event.target.value,
                }));

                setTotalNetValueApiParams({
                    index,
                    productId: productDetails?.productId,
                    baseAPIPrice: Number(formik.values.baseAPIPrice),
                    pricePerGram: Number(formik.values.products[index]?.goldAmountMYR || 0),
                    transactionType: transactionType,
                    discountId: formik.values.products[index].discountId || undefined,
                    quantity: event.target.value,
                });
            }
        }
    };

    const handleOnChangeNetValue = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
        formik.handleChange(event);

        if (amountOnBlurError) setAmountOnBlurError('');
        if (totalNetValueOnBlurError) setTotalNetValueOnBlurError('');

        if (event.target.value === '') {
            formik.setFieldValue(`products.[${index}]`, {
                ...formik.values.products[index],
                totalNetValue: '',
                amount: '',
                serialNo: '',
            });

            return;
        } else if (event.target.value === '0') {
            formik.setFieldValue(`products.[${index}]`, {
                ...formik.values.products[index],
                totalNetValue: 0,
                amount: '',
                serialNo: '',
            });

            return;
        } else {
            const productDetails = getSelectedProduct(formik.values.products[index].productId);

            if (productDetails) {
                dispatch(combinedActions.goldGetTotalNetValueOrQuantityAttempt({
                    index,
                    productId: productDetails.productId,
                    baseAPIPrice: Number(formik.values.baseAPIPrice),
                    pricePerGram: Number(formik.values.products[index]?.goldAmountMYR || 0),
                    transactionType: transactionType,
                    discountId: formik.values.products[index].discountId || undefined,
                    totalNetValue: event.target.value,
                }));

                setTotalNetValueApiParams({
                    index,
                    productId: productDetails.productId,
                    baseAPIPrice: Number(formik.values.baseAPIPrice),
                    pricePerGram: Number(formik.values.products[index]?.goldAmountMYR || 0),
                    transactionType: transactionType,
                    discountId: formik.values.products[index].discountId || undefined,
                    totalNetValue: event.target.value,
                });
            }
        }
    };

    const handleOnSelectDiscount = (option: TDropdownOption, index: number) => {
        const discount = discountList.find((item) => item.discountId === option.value);

        if (option.value === undefined || option.value === '') {
            formik.handleChange({ target: { name: `products.[${index}].discountId`, value: 0 } });
            formik.handleChange({ target: { name: `products.[${index}].discountCode`, value: '' } });
            formik.handleChange({ target: { name: `products.[${index}].discountName`, value: '' } });

            if (!!totalNetValueApiParams.quantity || !!totalNetValueApiParams.totalNetValue) {
                const productDetails = getSelectedProduct(formik.values.products[index].productId);

                if (productDetails) {
                    dispatch(combinedActions.goldGetTotalNetValueOrQuantityAttempt({
                        index,
                        productId: productDetails.productId,
                        baseAPIPrice: Number(formik.values.baseAPIPrice),
                        pricePerGram: Number(formik.values.products[index]?.goldAmountMYR || 0),
                        transactionType: transactionType,
                        discountId: undefined,
                        quantity: formik.values.products[index].amount,
                    }));

                    dispatch(combinedActions.goldGetTransactionPricePerGramValueAttempt({
                        index,
                        productId: productDetails.productId,
                        discountId: undefined,
                    }));
                }
            }
        }

        if (!discount) return;

        formik.handleChange({ target: { name: `products.[${index}].discountId`, value: discount.discountId } });
        formik.handleChange({ target: { name: `products.[${index}].discountCode`, value: discount.discountCode } });
        formik.handleChange({ target: { name: `products.[${index}].discountName`, value: discount.discountName } });

        if (!!totalNetValueApiParams.quantity || !!totalNetValueApiParams.totalNetValue) {
            if (!!totalNetValueApiParams.quantity || !!totalNetValueApiParams.totalNetValue) {
                const productDetails = getSelectedProduct(formik.values.products[index].productId);

                if (productDetails) {
                    dispatch(combinedActions.goldGetTotalNetValueOrQuantityAttempt({
                        index,
                        productId: productDetails.productId,
                        baseAPIPrice: Number(formik.values.baseAPIPrice),
                        pricePerGram: Number(formik.values.products[index]?.goldAmountMYR || 0),
                        transactionType: transactionType,
                        discountId: discount.discountId,
                        quantity: formik.values.products[index].amount,
                    }));

                    dispatch(combinedActions.goldGetTransactionPricePerGramValueAttempt({
                        index,
                        productId: productDetails.productId,
                        discountId: discount.discountId,
                    }));
                }
            }
        }
    };

    const handleQuantityOnBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        formik.handleBlur(event);

        if (Number(event.target.value) < 1) {
            setAmountOnBlurError('Quantity must be at least 1');
        }
    };

    const handleTotalNetValueOnBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        formik.handleBlur(event);

        if (Number(event.target.value) < 10) {
            setTotalNetValueOnBlurError('Value must be at least 10');
        }
    };

    const handleIsEarmarkingClicked = () => {
        formik.setValues({
            ...formik.values,
            isEarmarkTransaction: !formik.values.isEarmarkTransaction,
        });

        if (!formik.values.isEarmarkTransaction === true) {
            if (customerDetails.additionalUserDetails) {
                formik.handleChange({ target: { name: 'projectName', value: '' } });
            }
        }
        else {
            formik.handleChange({ target: { name: 'projectName', value: '' } });
            formik.handleChange({ target: { name: 'earmarkEndDate', value: '' } });
        }
    };

    const handleOnSelectProject = (option: TDropdownOption) => {
        if (formik.values.isEarmarkTransaction === true) {
            const selectedProject = projectFilters?.find(p => p.projectId === option.value);
            formik.handleChange({ target: { name: 'projectName', value: selectedProject?.projectName || '' } });
            formik.handleChange({ target: { name: 'projectId', value: option.value } });
        }
    };

    const handleOnChangeProject = (event: React.ChangeEvent<HTMLInputElement>) => {
        formik.handleChange({ target: { name: 'projectName', value: event.target.value } });
        if (!event.target.value) {
            formik.handleChange({ target: { name: 'projectId', value: 0 } });
        }
    };

    const handleOnSearchDiscount = (event: React.ChangeEvent<HTMLInputElement>) => {
        formik.handleChange({ target: { name: 'discountCode', value: event.target.value } });
    };

    const renderJointAccountDetails = () => {
        if (customerDetails.additionalUserDetails?.length && formik.values.name) {
            return (
                <div className={FormStyles['form-container']}>
                    {customerDetails.additionalUserDetails?.map((userDetails, index) => {
                        return (
                            <React.Fragment key={index}>
                                <LabelledInput
                                    id='name'
                                    name={`additionalUserDetails[${index}].name`}
                                    label='Child Name* :'
                                    value={userDetails.name || ''}
                                    // @ts-expect-error formik.errors.additionalUserDetails is not a string
                                    error={formik.touched.additionalUserDetails?.[index].name ? formik.errors.additionalUserDetails?.[index].name : undefined}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    className={FormStyles['span-2']}
                                    disabled
                                />

                                <LabelledDropdown
                                    id='idType'
                                    name={`additionalUserDetails[${index}].idType`}
                                    label='ID Type* :'
                                    options={idTypeOptions}
                                    value={CustomerIdTypeEnum[userDetails.idType] || ''}
                                    onChange={(e) => formik.setFieldValue(`additionalUserDetails[${index}].idType`, e.target.value)}
                                    onBlur={formik.handleBlur}
                                    autoComplete='off'
                                    disabled
                                    noOptionsMessage='No results found'
                                    optionSelected={!!CustomerIdTypeEnum[userDetails.idType.toString() as keyof typeof CustomerIdTypeEnum]}
                                />

                                <LabelledInput
                                    id='idNo'
                                    name={`additionalUserDetails[${index}].idNo`}
                                    label='ID No.* :'
                                    value={userDetails.idNo || ''}
                                    // @ts-expect-error formik.errors.additionalUserDetails is not a string
                                    error={formik.touched.additionalUserDetails?.[index].idNo ? formik.errors.additionalUserDetails?.[index].idNo : undefined}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    disabled
                                />

                                <LabelledDropdown
                                    id='nationality'
                                    name={`additionalUserDetails[${index}].nationality`}
                                    label='Nationality* :'
                                    options={nationalityOptions}
                                    value={userDetails.nationality || ''}
                                    // @ts-expect-error formik.errors.additionalUserDetails is not a string
                                    error={formik.touched.additionalUserDetails?.[index].nationality ? formik.errors.additionalUserDetails?.[index].nationality : undefined}
                                    onChange={(e) => formik.setFieldValue(`additionalUserDetails[${index}].nationality`, e.target.value)}
                                    onBlur={formik.handleBlur}
                                    autoComplete='off'
                                    noOptionsMessage='No country found'
                                    disabled
                                    optionSelected={!!nationalityOptions.find(option => option.value === userDetails.nationality)}
                                />

                                <LabelledDropdown
                                    id='race'
                                    name={`additionalUserDetails[${index}].race`}
                                    label='Race* :'
                                    options={raceOptions}
                                    value={CustomerRaceEnum[userDetails.race] || ''}
                                    onChange={(e) => formik.setFieldValue(`additionalUserDetails[${index}].race`, e.target.value)}
                                    onBlur={formik.handleBlur}
                                    autoComplete='off'
                                    disabled
                                    noOptionsMessage='No race found'
                                    optionSelected={!!raceOptions.find(option => option.value === userDetails.race.toString())}
                                />
                            </React.Fragment>
                        );
                    })}
                </div>
            );
        }

        return null;
    };

    const handleRemoveProductClicked = (index: number) => {
        if (formik.values.products.length > 1) {
            let currentProductsList = formik.values.products;
            currentProductsList = currentProductsList.filter((_, i) => i !== index);
            const productSearchArray = productSearch.filter((_, i) => i !== index);

            formik.setFieldValue('products', currentProductsList);
            setProductSearch(productSearchArray);

            const typedProductsList = currentProductsList.map((product) => ({
                ...product,
            }));

            dispatch(combinedActions.goldSetBuyOrSellGoldParams({
                ...formik.values,
                products: typedProductsList,
            }));
        }

        return;
    };

    const handleAddProductClicked = () => {
        setProductSearch([...productSearch, { productName: '' }]);

        formik.setFieldValue('products', [
            ...formik.values.products,
            {
                productId: 0,
                productName: '',
                metalType: 0,
                goldType: 0,
                silverType: 0,
                availableQuantity: 'Please select product first',
                collateralQuantity: 'Please select product first',
                amount: '',
                serialNo: '',
                goldAmountMYR: '',
                totalNetValue: '',
                discountId: 0,
                discountCode: '',
                discountName: '',
            },
        ]);

        // convert existing products' metalType before dispatching
        const typedExistingProducts = formik.values.products.map((product) => ({
            ...product,
        }));

        dispatch(combinedActions.goldSetBuyOrSellGoldParams({
            ...formik.values,
            products: [
                ...typedExistingProducts,
                {
                    productId: 0,
                    productName: '',
                    goldType: 0 as GoldTypeEnum,
                    silverType: 0 as SilverTypeEnum,
                    availableQuantity: 'Please select product first',
                    collateralQuantity: 'Please select product first',
                    amount: '',
                    serialNo: '',
                    goldAmountMYR: '',
                    totalNetValue: '',
                    discountId: 0,
                    discountCode: '',
                    discountName: '',
                },
            ],
        }));
    };

    const metalTypeOptions = Object.entries(MetalTypeEnum)
        .filter(([k]) => isNaN(Number(k)))
        .map(([label, value]) => ({ label, value: String(value) }));

    const handleOnSelectMetalType = (option: TDropdownOption, index: number) => {
        const selectedMetalType = Number(option.value);

        formik.setFieldValue('metalType', selectedMetalType);
        formik.setFieldValue(
            'baseAPIPrice',
            getBaseAPIPrice(selectedMetalType)
        );
        formik.setFieldValue(`products.[${index}].productId`, 0);
        formik.setFieldValue(`products.[${index}].productName`, '');

        dispatch(
            combinedActions.goldSetInventoryFilterParams({
                ...inventoryFilterParams,
                metalType: selectedMetalType,
                silverType: undefined,
                goldType: undefined,
            })
        );

        dispatch(combinedActions.goldGetAllInventoriesAttempt());

        const updatedFilters = productSearch.map((item, i) =>
            i === index ? { productName: '' } : item
        );
        setProductSearch(updatedFilters);
    };

    return (
        <div className={FormStyles.container}>
            <div className={FormStyles['filter-container']}>
                <LabelledDropdown
                    isFilter
                    label='Search for customer name or ID* : '
                    options={customerOptions}
                    value={name}
                    onChange={handleOnSearchCustomer}
                    onSelectOption={handleOnSelectCustomer}
                    noOptionsMessage={!name ? 'Search for customer name or ID' : 'No customer found'}
                    isLoading={gettingCustomers}
                    autoComplete='off'
                    optionSelected={!!customerOptions.find((option) => option.label === formik.values.name)}
                />
            </div>

            <form
                onSubmit={formik.handleSubmit}
                className={FormStyles.container}
            >
                <div className={FormStyles['form-container']}>
                    {
                        transactionType === TransactionActionEnum['Bank Sell'] && (
                            <>
                                <div>
                                    <div className={FormStyles['container-justify-end']}>
                                        <Checkbox
                                            isReversed
                                            label='Is Earmarking'
                                            isChecked={formik.values.isEarmarkTransaction}
                                            onChange={handleIsEarmarkingClicked}
                                        />

                                        <Text
                                            className={FormStyles['remarks-text']}
                                            label='(For EFS users only)'
                                            isBold
                                        />
                                    </div>
                                </div>

                                <div></div>

                                {
                                    formik.values.isEarmarkTransaction ? (
                                        <LabelledDropdown
                                            id='projectName'
                                            name='projectName'
                                            label='Select Project :'
                                            options={projectOptions}
                                            value={projectOptions.find(o => o.value === formik.values.projectId)?.label || formik.values.projectName}
                                            error={formik.touched.projectName ? formik.errors.projectName : undefined}
                                            onChange={handleOnChangeProject}
                                            onSelectOption={handleOnSelectProject}
                                            onBlur={formik.handleBlur}
                                            noOptionsMessage='No Project found'
                                            autoComplete='off'
                                            optionSelected={!!projectOptions.find((option) => option.value === projectFilters?.find(p => p.projectName === formik.values.projectName)?.projectId)}
                                        />
                                    ) : (
                                        <div></div>
                                    )
                                }
                            </>
                        )
                    }

                    <LabelledInput
                        id='name'
                        name='name'
                        label='Customer Name* :'
                        value={formik.values.name}
                        error={formik.touched.name ? formik.errors.name : undefined}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        disabled
                        className={FormStyles['span-2']}
                    />

                    <LabelledInput
                        id='idNo'
                        name='idNo'
                        label='ID Number* :'
                        value={formik.values.idNo}
                        error={formik.touched.idNo ? formik.errors.idNo : undefined}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        disabled
                    />

                    <LabelledInput
                        id='CIFNo'
                        name='CIFNo'
                        label='CIF No.* :'
                        value={formik.values.CIFNo}
                        error={formik.submitCount > 0 && formik.touched.CIFNo ? formik.errors.CIFNo : undefined}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        disabled
                    />

                    <LabelledDropdown
                        id='branchName'
                        name='branchName'
                        label='Branch* :'
                        options={branchOptions}
                        value={formik.values.branchName}
                        error={formik.touched.branchName ? formik.errors.branchName : undefined}
                        onChange={handleOnSearchBranch}
                        onSelectOption={handleOnSelectBranch}
                        onBlur={formik.handleBlur}
                        noOptionsMessage='No branches found'
                        autoComplete='off'
                        optionSelected={!!branchOptions.find((option) => option.label === formik.values.branchName)}
                        disabled={!allBranchAccess}
                    />

                    <LabelledInput
                        id='branchCode'
                        name='branchCode'
                        label='Branch Code* :'
                        value={formik.values.branchCode}
                        error={formik.submitCount > 0 && formik.touched.branchCode ? formik.errors.branchCode : undefined}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        disabled
                    />

                    <LabelledDropdown
                        id='region'
                        name='region'
                        label='Region* :'
                        options={regionOptions}
                        value={formik.values.region}
                        error={formik.submitCount > 0 && formik.touched.region ? formik.errors.region : undefined}
                        onChange={handleOnSearchRegion}
                        onSelectOption={handleOnSelectRegion}
                        onBlur={formik.handleBlur}
                        noOptionsMessage='No Region found'
                        className={FormStyles['span-2']}
                        autoComplete='off'
                        optionSelected={!!regionOptions.find((option) => option.label === formik.values.region)}
                        disabled
                    />
                </div>

                {
                    formik.values.products.map((newProduct, i) => (
                        <div key={i} className={FormStyles['products-container']}>
                            <div className={FormStyles['close-button-container']}>
                                <button
                                    className={FormStyles['close-button']}
                                    type='button'
                                    onClick={() => handleRemoveProductClicked(i)}
                                >
                                    <IoClose size={30} />
                                </button>
                            </div>

                            <div className={FormStyles['form-container']}>
                                <LabelledDropdown
                                    id='metalType'
                                    name='metalType'
                                    label='Metal Type *:'
                                    options={metalTypeOptions}
                                    value={metalTypeOptions.find((option) => Number(option.value) === formik.values.metalType)?.label ?? ''}
                                    error={formik.submitCount > 0 && !!formik.errors.metalType && formik.values.metalType < 1
                                        ? formik.errors.metalType as string
                                        : undefined}
                                    onSelectOption={(option) => handleOnSelectMetalType(option, i)}
                                    onBlur={formik.handleBlur}
                                    noOptionsMessage='No metal type found'
                                    autoComplete='off'
                                    optionSelected={!!metalTypeOptions.find((option) => Number(option.value) === formik.values.metalType)}
                                    disabled={!selectedBranchCode || !formik.values.customerId}
                                />

                                <div></div>

                                <LabelledDropdown
                                    id={`products.[${i}].productName`}
                                    name={`products.[${i}].productName`}
                                    label='Product Name *: '
                                    options={inventoryOptions(i).sort((a, b) => Number(a.value) - Number(b.value))}
                                    value={productSearch[i] ? productSearch[i].productName : ''}
                                    error={formik.submitCount > 0 && formik.touched.products?.[i]?.productName ? (formik.errors.products?.[i] as FormikErrors<TBuyOrSellMultipleProductsParams>)?.productName : undefined}
                                    onChange={(event) => handleOnSearchProduct(event, i)}
                                    onSelectOption={(event) => handleOnSelectProduct(event, i)}
                                    onBlur={formik.handleBlur}
                                    noOptionsMessage='No product found'
                                    autoComplete='off'
                                    optionSelected={!!inventoryOptions(i).find((option) => option.label === formik.values.products?.[i]?.productName)}
                                    disabled={!selectedBranchCode
                                        || !formik.values.customerId
                                        || !formik.values.metalType
                                        || (transactionType === TransactionActionEnum['Bank Buy'] && customerInventory.length === 0)}
                                />

                                <div className={FormStyles['available-balance']}>
                                    <Text
                                        label='Available balance:'
                                    />

                                    <Text
                                        label={newProduct.availableQuantity}
                                        isBold
                                    />

                                    <Text
                                        label='Collateral balance:'
                                    />

                                    <Text
                                        label={newProduct.collateralQuantity}
                                        isBold
                                    />
                                </div>

                                <LabelledInput
                                    id={`products.[${i}].amount`}
                                    name={`products.[${i}].amount`}
                                    label='Quantity* :'
                                    value={formik.values.products?.[i]?.amount}
                                    error={formik.touched.products?.[i]?.amount ? ((formik.errors.products?.[i] as FormikErrors<TBuyOrSellMultipleProductsParams>)?.amount || amountOnBlurError) : undefined}
                                    onChange={(event) => handleOnChangeQuantity(event, i)}
                                    onBlur={handleQuantityOnBlur}
                                    disabled={formik.values.products[i].productId === 0}
                                />

                                <LabelledInput
                                    id={`products.[${i}].goldAmountMYR`}
                                    name={`products.[${i}].goldAmountMYR`}
                                    label='Price per gram in MYR :'
                                    value={formik.values.products?.[i]?.goldAmountMYR}
                                    error={formik.submitCount > 0 && formik.touched.products?.[i]?.goldAmountMYR ? (formik.errors.products?.[i] as FormikErrors<TBuyOrSellMultipleProductsParams>)?.goldAmountMYR : undefined}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    placeholder='Select product to get price'
                                    disabled
                                />

                                <LabelledInput
                                    id={`products.[${i}].serialNo`}
                                    name={`products.[${i}].serialNo`}
                                    label={`Serial Number (comma delimited)${getIsSerialNoRequired(i) ? '*' : ''} :`}
                                    value={formik.values.products?.[i]?.serialNo}
                                    error={formik.touched.products?.[i]?.serialNo ? (formik.errors.products?.[i] as FormikErrors<TBuyOrSellMultipleProductsParams>)?.serialNo : undefined}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    disabled={getSelectedProduct(formik.values.products[i].productId)?.goldType === GoldTypeEnum.PaperGold || getSelectedProduct(formik.values.products[i].productId)?.silverType === SilverTypeEnum.PaperSilver ? true : false}
                                />

                                <LabelledInput
                                    id={`products.[${i}].totalNetValue`}
                                    name={`products.[${i}].totalNetValue`}
                                    label='Total Net Value MYR* :'
                                    value={formik.values.products?.[i]?.totalNetValue}
                                    error={formik.touched.products?.[i]?.totalNetValue ? ((formik.errors.products?.[i] as FormikErrors<TBuyOrSellMultipleProductsParams>)?.totalNetValue || totalNetValueOnBlurError) : undefined}
                                    onChange={(event) => handleOnChangeNetValue(event, i)}
                                    onBlur={handleTotalNetValueOnBlur}
                                    disabled={getSelectedProduct(formik.values.products[i].productId)?.goldType === GoldTypeEnum.PhysicalGold || getSelectedProduct(formik.values.products[i].productId)?.silverType === SilverTypeEnum.PhysicalSilver ? true : false}
                                />

                                <LabelledDropdown
                                    id='discountCode'
                                    name='discountCode'
                                    label='Discount Code :'
                                    placeholder='Select Discount'
                                    options={discountOptions}
                                    value={formik.values.products?.[i]?.discountCode}
                                    error={formik.touched.products?.[i]?.discountCode ? ((formik.errors.products?.[i] as FormikErrors<TBuyOrSellMultipleProductsParams>)?.discountCode || totalNetValueOnBlurError) : undefined}
                                    onSelectOption={(e) => handleOnSelectDiscount(e, i)}
                                    onBlur={formik.handleBlur}
                                    noOptionsMessage='No Discount Code found'
                                    autoComplete='off'
                                    optionSelected={!!discountOptions.find((option) => option.value === formik.values.products?.[i]?.discountId)}
                                />
                            </div>
                        </div>
                    ))
                }

                <ActionButton
                    label='+ Add Product'
                    type='button'
                    onClick={handleAddProductClicked}
                    disabled={formik.values.products[0].goldType === GoldTypeEnum.PaperGold || formik.values.products[0].silverType === SilverTypeEnum.PaperSilver}
                />

                <div className={FormStyles['form-container']}>
                    {transactionType === TransactionActionEnum['Bank Sell']
                        && (
                            <>
                                <LabelledInput
                                    id='salesPersonnelName'
                                    name='salesPersonnelName'
                                    label='Sales Personnel Name :'
                                    value={formik.values.salesPersonnelName}
                                    error={formik.submitCount > 0 && formik.touched.salesPersonnelName ? formik.errors.salesPersonnelName : undefined}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                />

                                <LabelledInput
                                    id='referralName'
                                    name='referralName'
                                    label='Referral Name :'
                                    value={formik.values.referralName}
                                    error={formik.submitCount > 0 && formik.touched.referralName ? formik.errors.referralName : undefined}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                />
                            </>
                        )
                    }

                    {
                        formik.values.isEarmarkTransaction && (
                            <LabelledDatepicker
                                id='earmarkEndDate'
                                name='earmarkEndDate'
                                label='End Date* :'
                                selectedDate={formik.values.earmarkEndDate}
                                error={endDateError !== '' ? endDateError : undefined}
                                setSelectedDate={(date: string) => formik.setFieldValue('earmarkEndDate', dayjs(date).toISOString())}
                                onBlur={formik.handleBlur}
                                minDate={dayjs().add(1, 'day').toISOString()}
                            />
                        )
                    }

                    <div className={`${FormStyles['joint-account-details-container']}  ${FormStyles['span-2']}`}>
                        {renderJointAccountDetails()}
                    </div>
                </div>

                <div className={`${FormStyles['button-container']} ${FormStyles['span-2']}`}>
                    <div className={FormStyles['button-subcontainer']}>
                        <ActionButton
                            label='Continue'
                            type='submit'
                            disabled={formik.isSubmitting || !!amountOnBlurError || !!totalNetValueOnBlurError}
                        />
                    </div>
                </div>
            </form >
        </div >
    );
};

export default BuyGoldForm;
